import { useState, useEffect, useCallback } from "react";
import {
  Box,
  Card,
  Typography,
  Grid,
  Stack,
  Chip,
  CircularProgress,
  Avatar,
  alpha,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableSortLabel,
  Pagination,
  TextField,
  MenuItem,
  InputAdornment,
  ToggleButton,
  ToggleButtonGroup,
  Alert,
  Link,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
  Tabs,
  Tab,
} from "@mui/material";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import CreditCardRoundedIcon from "@mui/icons-material/CreditCardRounded";
import ReceiptLongRoundedIcon from "@mui/icons-material/ReceiptLongRounded";
import PercentRoundedIcon from "@mui/icons-material/PercentRounded";
import PaidRoundedIcon from "@mui/icons-material/PaidRounded";
import SavingsRoundedIcon from "@mui/icons-material/SavingsRounded";
import SwapVertRoundedIcon from "@mui/icons-material/SwapVertRounded";
import AssessmentRoundedIcon from "@mui/icons-material/AssessmentRounded";
import {
  getReportSummary,
  getReportUsers,
  getReportUserTransactions,
} from "../api";

/* Role LEVELS 1–9 with their names (mirrors the backend role hierarchy). */
const ROLES = [
  { level: 1, name: "ADMIN" },
  { level: 2, name: "ADMIN_PARTNER" },
  { level: 3, name: "WHITE_LABEL" },
  { level: 4, name: "STATE_HEAD" },
  { level: 5, name: "SUPER_DISTRIBUTOR" },
  { level: 6, name: "MASTER_DISTRIBUTOR" },
  { level: 7, name: "DISTRIBUTOR" },
  { level: 8, name: "RETAILER" },
  { level: 9, name: "SHOPKEEPER" },
];

const ROLE_COLORS = {
  ADMIN: "#1c6b8c",
  ADMIN_PARTNER: "#2f8fb5",
  WHITE_LABEL: "#a855f7",
  STATE_HEAD: "#0ea5e9",
  SUPER_DISTRIBUTOR: "#06b6d4",
  MASTER_DISTRIBUTOR: "#14b8a6",
  DISTRIBUTOR: "#22c55e",
  RETAILER: "#f59e0b",
  SHOPKEEPER: "#f97316",
};

const RANGE_PRESETS = [
  { value: "today", label: "Today" },
  { value: "yesterday", label: "Yesterday" },
  { value: "last7", label: "Last 7 days" },
  { value: "month", label: "This month" },
  { value: "custom", label: "Custom" },
];

const LIMIT = 50;

const roleColor = (name) => ROLE_COLORS[name] || "#1c6b8c";

const formatINR = (val) =>
  `₹${Number(val || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;

const formatCount = (val) => Number(val || 0).toLocaleString("en-IN");

/* Signed currency, e.g. "+₹1,200" or "-₹350". */
const formatSignedINR = (val) => {
  const n = Number(val || 0);
  return `${n < 0 ? "-" : "+"}${formatINR(Math.abs(n))}`;
};

const formatDateTime = (val) =>
  val
    ? new Date(val).toLocaleString("en-IN", {
        dateStyle: "medium",
        timeStyle: "short",
      })
    : "—";

function RoleChip({ name }) {
  const label = (name ?? "").replace(/_/g, " ") || "—";
  const color = roleColor(name);
  return (
    <Chip
      size="small"
      label={label}
      sx={{
        bgcolor: alpha(color, 0.12),
        color,
        fontWeight: 600,
        fontSize: 11,
      }}
    />
  );
}

/* Sortable numeric column header. `col` is the sort key. */
function NumHeader({ col, label, sort, onSort }) {
  return (
    <TableCell align="right" sortDirection={sort === col ? "desc" : false}>
      <TableSortLabel
        active={sort === col}
        direction="desc"
        onClick={() => onSort(col)}
      >
        {label}
      </TableSortLabel>
    </TableCell>
  );
}

export default function AdminReports() {
  const [range, setRange] = useState("today");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [roleId, setRoleId] = useState("ALL");
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [sort, setSort] = useState("selfEarned");
  const [page, setPage] = useState(1);

  const [summary, setSummary] = useState(null);
  const [users, setUsers] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  /* Drill-down dialog state. */
  const [detailUser, setDetailUser] = useState(null);
  const [detailTab, setDetailTab] = useState(0);
  const [detailData, setDetailData] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState(null);

  /* Debounce the search input. */
  useEffect(() => {
    const t = setTimeout(() => {
      setDebouncedSearch(search);
      setPage(1);
    }, 400);
    return () => clearTimeout(t);
  }, [search]);

  /* Only send date range params when meaningfully complete. */
  const rangeReady = range !== "custom" || (from && to);

  const fetchData = useCallback(() => {
    if (!rangeReady) return;
    setLoading(true);
    setError(null);

    const rangeParams = { range };
    if (range === "custom") {
      rangeParams.from = from;
      rangeParams.to = to;
    }

    const userParams = {
      ...rangeParams,
      sort,
      page,
      limit: LIMIT,
    };
    if (roleId !== "ALL") userParams.roleId = roleId;
    if (debouncedSearch) userParams.search = debouncedSearch;

    Promise.all([getReportSummary(rangeParams), getReportUsers(userParams)])
      .then(([sumRes, usersRes]) => {
        setSummary(sumRes.data);
        setUsers(usersRes.data.users || []);
        setTotal(usersRes.data.pagination?.total || 0);
      })
      .catch((err) => {
        setError(err.response?.data?.error || "Failed to load reports");
      })
      .finally(() => setLoading(false));
  }, [range, from, to, roleId, debouncedSearch, sort, page, rangeReady]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  /* Fetch the drill-down transactions whenever the dialog opens for a user,
     using the same range params the aggregate table is currently showing. */
  useEffect(() => {
    if (!detailUser) return undefined;
    let active = true;
    setDetailLoading(true);
    setDetailError(null);
    setDetailData(null);

    const params = { range };
    if (range === "custom") {
      params.from = from;
      params.to = to;
    }

    getReportUserTransactions(detailUser.userId, params)
      .then((res) => {
        if (active) setDetailData(res.data);
      })
      .catch((err) => {
        if (active)
          setDetailError(
            err.response?.data?.error || "Failed to load transactions"
          );
      })
      .finally(() => {
        if (active) setDetailLoading(false);
      });

    return () => {
      active = false;
    };
  }, [detailUser, range, from, to]);

  const openDetails = (user, tab) => {
    setDetailTab(tab);
    setDetailUser(user);
  };

  const closeDetails = () => setDetailUser(null);

  const handleRange = (_, val) => {
    if (!val) return;
    setRange(val);
    setPage(1);
  };

  const handleSort = (col) => {
    setSort(col);
    setPage(1);
  };

  const totals = summary?.totals || {};
  const byRole = summary?.byRole || [];
  const rangeLabel = summary?.range?.label || "";

  const statCards = [
    {
      label: "Payins",
      value: formatINR(totals.payinVolume),
      sub: `${formatCount(totals.payinCount)} payins`,
      icon: <CreditCardRoundedIcon />,
      color: "#0ea5e9",
    },
    {
      label: "Payouts",
      value: formatINR(totals.payoutVolume),
      sub: `${formatCount(totals.payoutCount)} payouts`,
      icon: <ReceiptLongRoundedIcon />,
      color: "#6366f1",
    },
    {
      label: "Commissions Paid",
      value: formatINR(totals.commissionsPaid),
      sub: "to downline",
      icon: <PaidRoundedIcon />,
      color: "#2f8fb5",
    },
    {
      label: "Admin Earned (net)",
      value: formatINR(totals.adminEarned),
      sub: `payins ${formatINR(totals.adminPayinEarned)} · payouts ${formatINR(
        totals.adminPayoutEarned
      )}`,
      icon: <SavingsRoundedIcon />,
      color: "#22c55e",
    },
    {
      label: "Payout Charges",
      value: formatINR(totals.payoutChargeCollected),
      sub: `Gmax cost ${formatINR(totals.payoutGmaxCost)}`,
      icon: <PercentRoundedIcon />,
      color: "#f59e0b",
    },
    {
      label: "Adjustments (net)",
      value: formatSignedINR(totals.adjustmentsNet),
      valueColor:
        Number(totals.adjustmentsNet || 0) < 0 ? "error.main" : "success.main",
      sub: `▲ credit ${formatINR(totals.adjustmentsCredit)} · ▼ debit ${formatINR(
        totals.adjustmentsDebit
      )}`,
      icon: <SwapVertRoundedIcon />,
      color: "#ec4899",
    },
  ];

  const pageCount = Math.ceil(total / LIMIT);

  const detailPayins = detailData?.payins || [];
  const detailPayouts = detailData?.payouts || [];
  const detailLabel = detailData?.range?.label || rangeLabel;

  return (
    <Box>
      <Stack
        direction="row"
        alignItems="center"
        gap={1.5}
        mb={0.5}
        flexWrap="wrap"
      >
        <Typography variant="h5">Reports</Typography>
        {rangeLabel && (
          <Chip
            size="small"
            icon={<AssessmentRoundedIcon sx={{ fontSize: 16 }} />}
            label={rangeLabel}
            color="primary"
            variant="outlined"
          />
        )}
      </Stack>
      <Typography variant="body2" color="text.secondary" mb={3}>
        Earnings and transaction analytics by user and downline
      </Typography>

      {/* FILTER BAR */}
      <Card sx={{ p: 2, mb: 3 }}>
        <Stack
          direction="row"
          gap={1.5}
          flexWrap="wrap"
          alignItems="center"
        >
          <ToggleButtonGroup
            size="small"
            exclusive
            value={range}
            onChange={handleRange}
            sx={{ flexWrap: "wrap" }}
          >
            {RANGE_PRESETS.map((p) => (
              <ToggleButton
                key={p.value}
                value={p.value}
                sx={{ textTransform: "none", px: 1.5 }}
              >
                {p.label}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>

          {range === "custom" && (
            <>
              <TextField
                size="small"
                type="date"
                label="From"
                InputLabelProps={{ shrink: true }}
                value={from}
                onChange={(e) => {
                  setFrom(e.target.value);
                  setPage(1);
                }}
                sx={{ minWidth: 150 }}
              />
              <TextField
                size="small"
                type="date"
                label="To"
                InputLabelProps={{ shrink: true }}
                value={to}
                onChange={(e) => {
                  setTo(e.target.value);
                  setPage(1);
                }}
                sx={{ minWidth: 150 }}
              />
            </>
          )}

          <Box sx={{ flexGrow: 1 }} />

          <TextField
            select
            size="small"
            label="Role"
            value={roleId}
            onChange={(e) => {
              setRoleId(e.target.value);
              setPage(1);
            }}
            sx={{ minWidth: 170 }}
          >
            <MenuItem value="ALL">All Roles</MenuItem>
            {ROLES.map((r) => (
              <MenuItem key={r.level} value={r.level}>
                {r.name.replace(/_/g, " ")}
              </MenuItem>
            ))}
          </TextField>

          <TextField
            size="small"
            placeholder="Search name, email, phone..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchRoundedIcon
                    sx={{ fontSize: 20, color: "text.secondary" }}
                  />
                </InputAdornment>
              ),
            }}
            sx={{ minWidth: 240 }}
          />
        </Stack>
      </Card>

      {error && (
        <Alert
          severity="error"
          sx={{ mb: 3, borderRadius: 2 }}
          onClose={() => setError(null)}
        >
          {error}
        </Alert>
      )}

      {/* SUMMARY CARDS */}
      <Grid container spacing={2} mb={3}>
        {statCards.map((s) => (
          <Grid item xs={6} sm={4} md={2} key={s.label}>
            <Card sx={{ p: 2, height: "100%" }}>
              <Stack direction="row" alignItems="center" gap={1.5}>
                <Avatar
                  sx={{
                    bgcolor: alpha(s.color, 0.14),
                    color: s.color,
                    width: 40,
                    height: 40,
                  }}
                >
                  {s.icon}
                </Avatar>
                <Box sx={{ minWidth: 0 }}>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    noWrap
                  >
                    {s.label}
                  </Typography>
                  <Typography
                    variant="h6"
                    fontWeight={700}
                    fontSize={18}
                    color={s.valueColor}
                    sx={{ lineHeight: 1.25, overflowWrap: "anywhere" }}
                  >
                    {s.value}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" noWrap>
                    {s.sub}
                  </Typography>
                </Box>
              </Stack>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* PER-ROLE STRIP */}
      {byRole.length > 0 && (
        <Card sx={{ p: 2, mb: 3 }}>
          <Typography variant="subtitle2" fontWeight={600} mb={1.5}>
            Split by role
          </Typography>
          <Stack direction="row" gap={1.5} flexWrap="wrap">
            {byRole.map((r) => (
              <Box
                key={r.roleLevel ?? r.roleName}
                sx={{
                  p: 1.5,
                  minWidth: 180,
                  borderRadius: 2,
                  borderLeft: `3px solid ${roleColor(r.roleName)}`,
                  bgcolor: "action.hover",
                }}
              >
                <Stack
                  direction="row"
                  alignItems="center"
                  justifyContent="space-between"
                  gap={1}
                  mb={0.5}
                >
                  <RoleChip name={r.roleName} />
                  <Typography variant="caption" color="text.secondary">
                    {formatCount(r.payinCount)} payins
                  </Typography>
                </Stack>
                <Typography variant="body2">
                  Payin:{" "}
                  <Box component="span" fontWeight={600}>
                    {formatINR(r.payinVolume)}
                  </Box>
                </Typography>
                <Typography variant="body2">
                  Earned:{" "}
                  <Box component="span" fontWeight={600} color="success.main">
                    {formatINR(r.earned)}
                  </Box>
                </Typography>
              </Box>
            ))}
          </Stack>
        </Card>
      )}

      {/* USER TABLE */}
      <Card sx={{ overflow: "hidden" }}>
        {loading ? (
          <Box sx={{ p: 5, textAlign: "center" }}>
            <CircularProgress />
          </Box>
        ) : users.length === 0 ? (
          <Box sx={{ p: 5, textAlign: "center" }}>
            <AssessmentRoundedIcon
              sx={{ fontSize: 48, color: "text.disabled", mb: 1 }}
            />
            <Typography color="text.secondary">
              No activity in this range
            </Typography>
          </Box>
        ) : (
          <Box sx={{ overflowX: "auto" }}>
            <Table sx={{ minWidth: 1400 }}>
              <TableHead>
                <TableRow>
                  <TableCell>User</TableCell>
                  <TableCell>Role</TableCell>
                  <NumHeader
                    col="payinVolume"
                    label="Payins (own)"
                    sort={sort}
                    onSort={handleSort}
                  />
                  <NumHeader
                    col="payoutVolume"
                    label="Payouts (own)"
                    sort={sort}
                    onSort={handleSort}
                  />
                  <NumHeader
                    col="selfEarned"
                    label="Self earned"
                    sort={sort}
                    onSort={handleSort}
                  />
                  <NumHeader
                    col="downlineEarned"
                    label="Downline earned"
                    sort={sort}
                    onSort={handleSort}
                  />
                  <NumHeader
                    col="downlineVolume"
                    label="Downline payin"
                    sort={sort}
                    onSort={handleSort}
                  />
                  <NumHeader
                    col="adminEarned"
                    label="Admin earned"
                    sort={sort}
                    onSort={handleSort}
                  />
                  <NumHeader
                    col="adjustments"
                    label="Adjustments"
                    sort={sort}
                    onSort={handleSort}
                  />
                  <TableCell align="right">Direct downline</TableCell>
                  <TableCell align="right">Wallet Balance</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {users.map((u) => {
                  const own = u.own || {};
                  const dl = u.downline || {};
                  const initials = (u.name || "")
                    .split(" ")
                    .map((p) => p[0])
                    .filter(Boolean)
                    .slice(0, 2)
                    .join("");
                  return (
                    <TableRow key={u.userId} hover>
                      <TableCell>
                        <Stack direction="row" alignItems="center" gap={1.5}>
                          <Avatar
                            sx={{
                              width: 32,
                              height: 32,
                              bgcolor: roleColor(u.roleName),
                              fontSize: 13,
                              fontWeight: 700,
                            }}
                          >
                            {initials.toUpperCase()}
                          </Avatar>
                          <Box>
                            <Typography variant="body2" fontWeight={600}>
                              {u.name || "—"}
                            </Typography>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              {u.email || u.mobile || "—"}
                            </Typography>
                          </Box>
                        </Stack>
                      </TableCell>
                      <TableCell>
                        <RoleChip name={u.roleName} />
                      </TableCell>
                      <TableCell align="right">
                        <Link
                          component="button"
                          type="button"
                          underline="hover"
                          color="inherit"
                          onClick={() => openDetails(u, 0)}
                          sx={{
                            display: "block",
                            textAlign: "right",
                            width: "100%",
                            "&:hover": { color: "primary.main" },
                          }}
                        >
                          <Typography variant="body2" fontWeight={600}>
                            {formatINR(own.payinVolume)}
                          </Typography>
                          <Typography
                            variant="caption"
                            color="text.secondary"
                          >
                            {formatCount(own.payinCount)} txns
                          </Typography>
                        </Link>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2" fontWeight={600}>
                          {formatINR(own.payoutVolume)}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {formatCount(own.payoutCount)} txns
                        </Typography>
                      </TableCell>
                      <TableCell
                        align="right"
                        sx={{ fontWeight: 600, color: "success.main" }}
                      >
                        {formatINR(own.selfEarned)}
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600 }}>
                        {formatINR(dl.earned)}
                      </TableCell>
                      <TableCell align="right">
                        {formatINR(dl.payinVolume)}
                      </TableCell>
                      <TableCell
                        align="right"
                        sx={{ fontWeight: 600, color: "primary.main" }}
                      >
                        {formatINR(own.adminEarnedFromUser)}
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600 }}>
                        {Number(own.adjustmentsNet || 0) === 0 ? (
                          <Box component="span" sx={{ color: "text.disabled" }}>
                            —
                          </Box>
                        ) : (
                          <Box
                            component="span"
                            sx={{
                              color:
                                Number(own.adjustmentsNet) < 0
                                  ? "error.main"
                                  : "success.main",
                            }}
                          >
                            {formatSignedINR(own.adjustmentsNet)}
                          </Box>
                        )}
                      </TableCell>
                      <TableCell align="right">
                        {formatCount(u.directDownlineCount)}
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2" fontWeight={600}>
                          {formatINR(u.balance?.main)}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Comm: {formatINR(u.balance?.commission)}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </Box>
        )}
        {!loading && pageCount > 1 && (
          <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
            <Pagination
              count={pageCount}
              page={page}
              onChange={(_, v) => setPage(v)}
              color="primary"
            />
          </Box>
        )}
      </Card>

      {/* DRILL-DOWN DIALOG */}
      <Dialog
        open={Boolean(detailUser)}
        onClose={closeDetails}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle sx={{ pr: 6 }}>
          <Typography variant="h6" component="span" fontWeight={700}>
            {detailUser?.name || "—"}
          </Typography>
          <Typography
            variant="body2"
            color="text.secondary"
            component="span"
            sx={{ ml: 1 }}
          >
            · {(detailUser?.roleName || "—").replace(/_/g, " ")}
            {detailLabel ? ` — ${detailLabel}` : ""}
          </Typography>
          <IconButton
            onClick={closeDetails}
            size="small"
            sx={{ position: "absolute", top: 12, right: 12 }}
          >
            <CloseRoundedIcon />
          </IconButton>
        </DialogTitle>
        <Tabs
          value={detailTab}
          onChange={(_, v) => setDetailTab(v)}
          sx={{ px: 3, borderBottom: 1, borderColor: "divider" }}
        >
          <Tab label={`Payins (${detailPayins.length})`} />
          <Tab label={`Payouts (${detailPayouts.length})`} />
        </Tabs>
        <DialogContent dividers>
          {detailLoading ? (
            <Box sx={{ p: 5, textAlign: "center" }}>
              <CircularProgress />
            </Box>
          ) : detailError ? (
            <Alert severity="error" sx={{ borderRadius: 2 }}>
              {detailError}
            </Alert>
          ) : detailTab === 0 ? (
            detailPayins.length === 0 ? (
              <Box sx={{ p: 4, textAlign: "center" }}>
                <Typography color="text.secondary">
                  No payins in this range
                </Typography>
              </Box>
            ) : (
              <Box sx={{ overflowX: "auto" }}>
                <Table size="small" sx={{ minWidth: 700 }}>
                  <TableHead>
                    <TableRow>
                      <TableCell>Date</TableCell>
                      <TableCell align="right">Amount (₹)</TableCell>
                      <TableCell>Gateway</TableCell>
                      <TableCell>Category</TableCell>
                      <TableCell>Card</TableCell>
                      <TableCell>Customer</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {detailPayins.map((p) => (
                      <TableRow key={p.id} hover>
                        <TableCell>{formatDateTime(p.createdAt)}</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 600 }}>
                          {formatINR(p.amount)}
                        </TableCell>
                        <TableCell>{p.gateway || "—"}</TableCell>
                        <TableCell>{p.category || "—"}</TableCell>
                        <TableCell>
                          {p.cardLast4
                            ? `•••• ${p.cardLast4}${
                                p.cardBank ? ` · ${p.cardBank}` : ""
                              }`
                            : "—"}
                        </TableCell>
                        <TableCell>{p.customerName || "—"}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Box>
            )
          ) : detailPayouts.length === 0 ? (
            <Box sx={{ p: 4, textAlign: "center" }}>
              <Typography color="text.secondary">
                No payouts in this range
              </Typography>
            </Box>
          ) : (
            <Box sx={{ overflowX: "auto" }}>
              <Table size="small" sx={{ minWidth: 700 }}>
                <TableHead>
                  <TableRow>
                    <TableCell>Order ID</TableCell>
                    <TableCell>Date</TableCell>
                    <TableCell>Wallet</TableCell>
                    <TableCell align="right">Amount (₹)</TableCell>
                    <TableCell align="right">Charge (₹)</TableCell>
                    <TableCell>To account</TableCell>
                    <TableCell>Status</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {detailPayouts.map((p) => (
                    <TableRow key={p.id} hover>
                      <TableCell sx={{ fontFamily: "monospace", fontWeight: 700 }}>
                        {p.orderNo || "—"}
                      </TableCell>
                      <TableCell>{formatDateTime(p.createdAt)}</TableCell>
                      <TableCell>
                        {p.walletType === "main" ? "Main" : "Commission"}
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600 }}>
                        {formatINR(p.amount)}
                      </TableCell>
                      <TableCell align="right">{formatINR(p.charge)}</TableCell>
                      <TableCell>
                        {p.accountLast4 ? `•••• ${p.accountLast4}` : "—"}
                        {p.ifsc ? ` · ${p.ifsc}` : ""}
                      </TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          label={p.status}
                          color={
                            p.status === "completed"
                              ? "success"
                              : p.status === "failed" || p.status === "rejected"
                                ? "error"
                                : "warning"
                          }
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDetails}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
